/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, February 7th 2022, 9:22:29 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { UseFetchListArgs, APIMakeRequestFncArgs, useAPIRequest } from "lib/api";
import { PrimaryKeyValue, ObjectValue, ObjectKey } from "lib/types";
import { useDeepCallback } from "lib/deep_hooks";
import { sortBy } from "lodash";
import { useDeepMemo } from "lib/deep_hooks";
import {
  LIST_EDITOR_ACTIONS,
  ItemsListFilterFunction,
  CreationQueueItem,
  UpdateListOptions,
} from "./state/list-editor-actions";
import { itemsListReducer, ItemsListReducer, ItemsListReducerState } from "./state/list-editor-reducer";

/**Drag */
import { UseDragDropListItemArgs } from "ui-widgets/drag-and-drop";
import { invariant } from "immutability-helper";
/**Drag */

const getInitialItemsListState = <T,>(
  uniqueIdentifier: ObjectKey<T>,
  positionLabel: ObjectKey<T>
): ItemsListReducerState<T> => {
  return {
    selectedItemId: null,
    savedItems: [],
    filteredItemIds: null,
    itemsLoaded: false,
    itemCreationQueue: [],
    config: { uniqueIdentifier, positionLabel },
  };
};

interface FilterItemsOptions<T> {
  filteredItemIds: ItemsListReducerState<T>["filteredItemIds"];
  items: T[];
  uniqueIdentifier: PrimaryKeyValue<T>;
}
const filterItems = <T,>({ filteredItemIds, items, uniqueIdentifier }: FilterItemsOptions<T>): T[] => {
  if (!filteredItemIds) return items;

  if (filteredItemIds.length === 0) return [];

  return items.filter((tt) => filteredItemIds.includes(tt[uniqueIdentifier]));
};

export interface IListEditorContext<T> extends Pick<UseDragDropListItemArgs<T>, "handleDragEnter" | "handleDrop"> {
  callFilteringFunction: (listFilterFunction: ItemsListFilterFunction<T>) => void;
  itemsListState: ItemsListReducerState<T>;
  isFetchingItems: boolean;
  fetchItemsStatusCode: number;
  fetchItemsHasError: boolean;
  filteredItems: T[];
  loadItems: (itemsList: T[]) => void;
  selectItem: (id: PrimaryKeyValue<T>) => void;
  findItem: (id: PrimaryKeyValue<T>) => { item: T; index: number };
  removeItem: (itemToRemoveId: PrimaryKeyValue<T>) => void;
  updateList: (itemToUpdate: T, options?: UpdateListOptions<T>) => void;
  updateListAndSelectIfNewItem: (itemToUpdate: T) => void;
  createNewItem: (newItem: T) => void;
  dequeueNewItem: (newItem: T) => void;
  currentMutationItem: T;
  creationQueueHasItems: boolean;
  handleDragStart: () => void;
  startOfListPosition: number;
  endOfListPosition: number;
}

// Conext here is a static variable as per react documentation. Because of this, we give it a generic type 'any'
// The actual type will be resolved by the component implementing the ListEditorProvider below.
export const ListEditorContext = React.createContext<IListEditorContext<any>>({
  callFilteringFunction: null,
  itemsListState: getInitialItemsListState(null, null),
  isFetchingItems: false,
  fetchItemsStatusCode: 0,
  fetchItemsHasError: false,
  filteredItems: null,
  loadItems: null,
  selectItem: null,
  findItem: null,
  removeItem: null,
  updateList: null,
  updateListAndSelectIfNewItem: null,
  createNewItem: null,
  dequeueNewItem: null,
  handleDragStart: null,
  handleDrop: null,
  handleDragEnter: null,
  currentMutationItem: null,
  creationQueueHasItems: null,
  startOfListPosition: 0,
  endOfListPosition: 0,
});

export const useListEditorContext = <T,>() => {
  return React.useContext<IListEditorContext<T>>(ListEditorContext as unknown as React.Context<IListEditorContext<T>>);
};

type APIFetchListSpec<T> = Omit<UseFetchListArgs<T>, "makeRequestOnInitialMount"> &
  Partial<Pick<UseFetchListArgs<T>, "makeRequestOnInitialMount">>;

// useAPIReorderListItem is a prop whose value is a hook. Using dependency injection we allow the
// ListEditor provider to be agnostic of the implementation detail of the reordering API request.
type DragDropProps<T, EnableDragging> = EnableDragging extends true
  ? {
      positionLabel: ObjectKey<T>;
      useAPIReorderListItem: () => {
        putData: (args: APIMakeRequestFncArgs<any>) => void;
        [key: string]: any;
      };
    }
  : { positionLabel?: null; useAPIReorderListItem?: null };

export type ListEditorProviderProps<T, EnableDragging extends boolean> = APIFetchListSpec<T> &
  DragDropProps<T, EnableDragging> & {
    children: React.ReactNode;
    uniqueIdentifier: ObjectKey<T>;
    initialItems?: T[];
    removeItemCallback?: (itemId: PrimaryKeyValue<T>) => void;
  };

export const ListEditorProvider = <T, EnableDragging extends boolean>({
  children,
  uniqueIdentifier,
  positionLabel,
  useAPIReorderListItem,
  apiServicePath,
  apiPathParams,
  apiQueryParams,
  dependencyList = [],
  makeRequestOnInitialMount = true,
  initialItems,
  removeItemCallback,
}: ListEditorProviderProps<T, EnableDragging>) => {
  const [itemsListState, dispatchListEditorAction] = React.useReducer<ItemsListReducer<T>>(
    itemsListReducer,
    getInitialItemsListState(uniqueIdentifier, positionLabel)
  );

  invariant(
    itemsListState.savedItems.length === 0 ||
      !positionLabel ||
      typeof itemsListState.savedItems[0]?.[positionLabel] === "number",
    () =>
      "List Editor Provider Error: position label must index an integer value that is sequential and can be used for sorting items."
  );

  const loadItems = React.useCallback(
    (itemsList: T[]) =>
      dispatchListEditorAction({
        actionType: LIST_EDITOR_ACTIONS.LOAD_ITEMS,
        payload: { fetchedItems: itemsList },
      }),
    [dispatchListEditorAction]
  );

  const { fetchItemsHasError, fetchItemsStatusCode, isFetchingItems } = useListEditorLoadItems(
    loadItems,
    initialItems,
    {
      apiServicePath,
      apiPathParams,
      apiQueryParams,
      dependencyList,
      makeRequestOnInitialMount,
    }
  );

  const selectItem = React.useCallback(
    (selectedItemId: PrimaryKeyValue<T>) => {
      dispatchListEditorAction({
        actionType: LIST_EDITOR_ACTIONS.SELECT_ITEM,
        payload: { selectedItemId },
      });
    },
    [dispatchListEditorAction]
  );

  const removeItem = React.useCallback(
    (itemToRemoveId: PrimaryKeyValue<T>) => {
      dispatchListEditorAction({
        actionType: LIST_EDITOR_ACTIONS.REMOVE_ITEM,
        payload: { itemToRemoveId },
      });

      removeItemCallback?.(itemToRemoveId); // Must be a properly implemented callback
    },
    [dispatchListEditorAction, removeItemCallback]
  );

  const createNewItem = React.useCallback(
    (newItem: T) => {
      dispatchListEditorAction({
        actionType: LIST_EDITOR_ACTIONS.CREATE_ITEM,
        payload: { newItem },
      });
    },
    [dispatchListEditorAction]
  );

  const dequeueNewItem = React.useCallback(
    (newItemTempId: CreationQueueItem<T>["os_unsaved_item_temp_unique_id"]) => {
      dispatchListEditorAction({
        actionType: LIST_EDITOR_ACTIONS.DEQUEUE_NEW_ITEM,
        payload: { newItemTempId },
      });
    },
    [dispatchListEditorAction]
  );

  const callFilteringFunction = React.useCallback(
    (listFilterFunction: ItemsListFilterFunction<T>) => {
      dispatchListEditorAction({
        actionType: LIST_EDITOR_ACTIONS.FILTER_LIST,
        listFilterFunction,
      });
    },
    [dispatchListEditorAction]
  );

  const updateList = React.useCallback(
    (itemToUpdate: T, options?: UpdateListOptions<T>) =>
      dispatchListEditorAction({
        actionType: LIST_EDITOR_ACTIONS.UPDATE_LIST,
        payload: { itemToUpdate, options },
      }),
    [dispatchListEditorAction]
  );

  const findItem = useDeepCallback(
    (id: PrimaryKeyValue<T>) => {
      const item = itemsListState.savedItems.find((i) => i[uniqueIdentifier] === id);
      return {
        item,
        index: itemsListState.savedItems.indexOf(item),
      };
    },
    [itemsListState.savedItems]
  );

  const _currentNewItem = itemsListState.itemCreationQueue[0]; // FIFO

  // Using the normal useMemo so that we actually update a new object whenever it changes even by reference i.e. when multiple
  // new objects with similar values are added continuously. This should be permissible unless we get a use
  // case where that is not that case and then this provider can be extended and modified.
  const currentMutationItem = React.useMemo(
    () => _currentNewItem ?? findItem(itemsListState.selectedItemId).item,
    [itemsListState.selectedItemId, findItem, _currentNewItem]
  );

  const creationQueueHasItems = itemsListState.itemCreationQueue.length > 0;

  // Select the item if it is a newly added item. Helpful function when a new item is added and one
  // wants to select it as soon as it is updated in the list the API backend.
  const updateListAndSelectIfNewItem = React.useCallback(
    (itemToUpdate: T) => {
      updateList(itemToUpdate); // Update the item in the list
      if (creationQueueHasItems && itemToUpdate[uniqueIdentifier]) selectItem(itemToUpdate[uniqueIdentifier]); // Select the new item. Reducer will update this state together with update list above.
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [updateList, selectItem, creationQueueHasItems]
  );

  const handleDragStart = React.useCallback(() => {
    if (window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }
  }, []);

  const handleDragEnter = React.useCallback(
    (sourceItemId: PrimaryKeyValue<T>, hoverPosition: ObjectValue<T> & number) => {
      if (sourceItemId && typeof hoverPosition === "number") {
        dispatchListEditorAction({
          actionType: LIST_EDITOR_ACTIONS.HANDLE_DRAG_ENTER,
          payload: { sourceItemId, hoverPosition },
        });
      }
    },
    [dispatchListEditorAction]
  );

  // Let the putData function throw an error if useAPIReorderListItem hook is not provided.
  // Typescript communicates to the developer that this is needed when dragging is enabled i.e. type generic
  // EnableDragging = true
  const { putData: apiUpdatePosition } = useAPIReorderListItem?.() ?? { putData: null };

  const handleDrop = React.useCallback(
    (sourceItemId: PrimaryKeyValue<T>, targetItemId: PrimaryKeyValue<T>, targetPosition: ObjectValue<T>) => {
      if (sourceItemId && targetItemId && typeof targetPosition == "number") {
        apiUpdatePosition({
          apiPathParams: [sourceItemId],
          body: JSON.stringify({
            target_item_id: targetItemId,
          }),
          setValidAPIResponse: updateList,
        });
      }
    },
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [findItem, apiUpdatePosition]
  );

  // Returning the list sorted by the position so that one can always get the lowest to highest position
  // without traversing the whole list that would be an expensive operation. This also just feels more
  // intuitive and less contrived than having a list with position values in non-sequential indices.
  const savedItems = useDeepMemo(() => sortBy(itemsListState.savedItems, [positionLabel]), [itemsListState.savedItems]);

  const filteredItems = useDeepMemo(
    () =>
      filterItems({
        items: savedItems,
        filteredItemIds: itemsListState.filteredItemIds,
        uniqueIdentifier: itemsListState.config.uniqueIdentifier,
      }),
    [itemsListState.filteredItemIds, savedItems]
  );

  const startOfListPosition = useDeepMemo(
    () => (filteredItems[0]?.[positionLabel] ?? 0) as unknown as number,
    [filteredItems]
  );

  const endOfListPosition = useDeepMemo(() => {
    if (itemsListState.itemCreationQueue.length > 0) {
      const { length } = itemsListState.itemCreationQueue;
      return ((itemsListState.itemCreationQueue[length - 1][positionLabel] ?? -1) as number) + 1;
    }
    return ((filteredItems[filteredItems.length - 1]?.[positionLabel] ?? -1) as number) + 1;
  }, [filteredItems, itemsListState.itemCreationQueue.length]);

  /** DnD */

  return (
    <ListEditorContext.Provider
      value={{
        itemsListState: { ...itemsListState, savedItems },
        isFetchingItems,
        fetchItemsStatusCode,
        fetchItemsHasError,
        filteredItems,
        loadItems,
        selectItem,
        findItem,
        removeItem,
        createNewItem,
        dequeueNewItem,
        updateList,
        updateListAndSelectIfNewItem,
        currentMutationItem,
        creationQueueHasItems,
        handleDragStart,
        handleDragEnter,
        handleDrop,
        callFilteringFunction,
        startOfListPosition,
        endOfListPosition,
      }}
    >
      {children}
    </ListEditorContext.Provider>
  );
};

const useListEditorLoadItems = <T,>(
  loadItems: IListEditorContext<T>["loadItems"],
  initialItems: T[] = null,
  { apiServicePath, apiPathParams, apiQueryParams, dependencyList, makeRequestOnInitialMount }: APIFetchListSpec<T>
) => {
  React.useEffect(() => {
    if (initialItems) {
      loadItems(initialItems);
    }
  }, [loadItems, initialItems]);

  const {
    isMakingAPIRequest: isFetchingItems,
    httpResponseStatusCode: fetchItemsStatusCode,
    isAPIError: fetchItemsHasError,
    makeRequest,
  } = useAPIRequest<T[], T, T>({ apiServicePath, makeRequestOnInitialMount });

  React.useEffect(() => {
    if (initialItems !== null) return;

    if (!makeRequestOnInitialMount) return;

    makeRequest({
      apiQueryParams,
      apiPathParams,
      setValidAPIResponse: (fetchedItems: T[]) => loadItems(fetchedItems),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    loadItems,
    initialItems,
    makeRequest,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(apiQueryParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(apiPathParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...(dependencyList ?? []),
    makeRequestOnInitialMount,
  ]);

  return { isFetchingItems: !initialItems && isFetchingItems, fetchItemsStatusCode, fetchItemsHasError };
};
